import React, { useState } from "react";
import {
  Badge,
  Button,
  Calendar,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@lemonadetraining/web-ui";
import { IconCalendar, IconX } from "@tabler/icons-react";
import { format } from "date-fns";
import { gymDayKey, gymKeyMidnight, keyToLocalDate, localDateToKey } from "../lib/gymClock";
import { useGymTodayKey } from "../hooks/useGymTodayKey";

interface DatePickerWithChipProps {
  value?: Date | { toDate: () => Date } | null;
  onGetDateObj?: (date: Date | null) => void;
  editMode?: boolean;
  /** The gym's zone: days are picked and shown on its calendar. Null = this browser's calendar. */
  zone: string | null;
}

const calendarDay = (date: Date, zone: string | null): Date => {
  const key = gymDayKey(date, zone);
  return key ? keyToLocalDate(key) : date;
};

const DatePickerWithChip: React.FC<DatePickerWithChipProps> = (props) => {
  const [date, setDate] = useState<Date | null>(() => {
    if (props.value) {
      if (typeof props.value === "object" && "toDate" in props.value) {
        return props.value.toDate();
      }
      return props.value as Date;
    }
    return null;
  });

  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const todayKey = useGymTodayKey(props.zone);
  const today = todayKey ? keyToLocalDate(todayKey) : undefined;
  const shown = date ? calendarDay(date, props.zone) : undefined;

  const change = (next: Date | null) => {
    setDate(next);
    props.onGetDateObj?.(next);
  };

  return (
    <div className="flex flex-row items-center justify-end">
      {date && (
        <Badge variant="default" className="gap-1 pr-1">
          {format(calendarDay(date, props.zone), "d MMMM yyyy")}
          {props.editMode && (
            <button
              type="button"
              onClick={() => change(null)}
              className="ml-0.5 rounded-full hover:bg-primary-foreground/20"
              aria-label="Remove date"
            >
              <IconX size={14} />
            </button>
          )}
        </Badge>
      )}
      {props.editMode && !date && (
        <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm">
              <IconCalendar className="mr-2 h-4 w-4" />
              Pick a date
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={shown}
              defaultMonth={shown ?? today}
              today={today}
              onSelect={(newDate: Date | undefined) => {
                if (newDate) {
                  change(
                    props.zone ? gymKeyMidnight(localDateToKey(newDate), props.zone) : newDate
                  );
                }
                setIsCalendarOpen(false);
              }}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
};

export default DatePickerWithChip;
